import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getVendorMenu, createVendorOrder } from '../services/squareMenuService';
import { getCloverVendorMenu, createCloverVendorOrder } from '../services/cloverMenuService';

// Styled components
const VendorMenuContainer = styled.div`
  font-family: 'Arial', sans-serif;
  background-color: #0a0a0a;
  min-height: 100vh;
  color: #ffffff;
  background: linear-gradient(rgba(10, 10, 10, 0.95), rgba(10, 10, 10, 0.95)), 
              radial-gradient(circle at 30% 20%, rgba(212, 175, 55, 0.08), transparent 40%),
              radial-gradient(circle at 70% 60%, rgba(184, 134, 11, 0.05), transparent 50%);
  padding: 20px;
  border-radius: 12px;
  max-height: 600px;
  overflow-y: auto;
`;

const MenuHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const VendorName = styled.h1`
  font-size: 2.2em;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #d4af37, #f5d76e, #926f34);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: none;
`;

const MenuSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  margin: 0;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #d4af37;
`;

const ErrorMessage = styled.div`
  background-color: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  color: #dc3545;
  padding: 15px;
  border-radius: 8px;
  margin: 20px 0;
  text-align: center;
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  justify-content: center;
`;

const CategoryTab = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  font-size: 14px;
  background: ${props => props.$active ? 
    'linear-gradient(135deg, #d4af37, #f5d76e, #926f34)' : 
    'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.$active ? '#0a0a0a' : 'rgba(255, 255, 255, 0.9)'};
  border: 1px solid ${props => props.$active ? 'transparent' : 'rgba(224, 184, 65, 0.3)'};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${props => props.$active ? 
      '0 4px 15px rgba(212, 175, 55, 0.3)' : 
      '0 2px 8px rgba(0, 0, 0, 0.3)'};
  }
`;

const MenuItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const MenuItem = styled.div`
  background-color: #1a1a1a;
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(212, 175, 55, 0.15);
    border-color: #d4af37;
  }
`;

const ItemName = styled.h3`
  color: #d4af37;
  margin: 0 0 10px 0;
  font-size: 18px;
`;

const ItemDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 15px 0;
  font-size: 14px;
  line-height: 1.4;
`;

const ItemPrice = styled.div`
  color: #f5d76e;
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 15px;
`;

const AddToCartButton = styled.button`
  background: linear-gradient(135deg, #d4af37, #f5d76e, #926f34);
  color: #0a0a0a;
  border: none;
  padding: 10px 20px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  font-size: 14px;
  width: 100%;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CartSection = styled.div`
  background-color: #1a1a1a;
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
`;

const CartTitle = styled.h3`
  color: #d4af37;
  margin: 0 0 15px 0;
  font-size: 18px;
`;

const CartItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const CartItemInfo = styled.div`
  flex: 1;
`;

const CartItemName = styled.div`
  color: #ffffff;
  font-weight: bold;
`;

const CartItemQuantity = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
`;

const CartItemPrice = styled.div`
  color: #f5d76e;
  font-weight: bold;
`;

const CartTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-top: 2px solid rgba(212, 175, 55, 0.3);
  margin-top: 15px;
  font-size: 18px;
  font-weight: bold;
`;

const CheckoutButton = styled.button`
  background: linear-gradient(135deg, #d4af37, #f5d76e, #926f34);
  color: #0a0a0a;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  font-size: 16px;
  width: 100%;
  margin-top: 15px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(224, 184, 65, 0.3);
  padding: 12px 25px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  font-size: 14px;
  margin-top: 20px;
  
  &:hover {
    background: rgba(212, 175, 55, 0.1);
    border-color: #d4af37;
  }
`;

const VendorMenu = ({ vendor, onClose, onOrderComplete, globalCart, onAddToGlobalCart, onRemoveFromGlobalCart, onUpdateGlobalCartQuantity, getCartItemsForVendor }) => {
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All Items');
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  
  // Get cart items for this specific vendor
  const cart = getCartItemsForVendor ? getCartItemsForVendor(vendor.id) : [];

  useEffect(() => {
    const loadMenu = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let data;
        if (vendor.posSystem === 'clover') {
          data = await getCloverVendorMenu(vendor.id);
        } else {
          data = await getVendorMenu(vendor.id);
        }
        
        setMenuData(data);
        
        // Set first category as default
        const categories = Object.keys(data.menu);
        if (categories.length > 0) {
          setSelectedCategory(categories[0]);
        }
        
      } catch (err) {
        console.error('Error loading vendor menu:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (vendor && vendor.id) {
      loadMenu();
    }
  }, [vendor]);

  const addToCart = (item) => {
    if (onAddToGlobalCart) {
      onAddToGlobalCart(item, vendor);
    }
  };

  const removeFromCart = (itemId) => {
    if (onRemoveFromGlobalCart) {
      onRemoveFromGlobalCart(itemId);
    }
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (onUpdateGlobalCartQuantity) {
      onUpdateGlobalCartQuantity(itemId, newQuantity);
    }
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      return total + (parseFloat(item.price) * item.quantity);
    }, 0).toFixed(2);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    
    try {
      setIsProcessingOrder(true);
      
      const orderData = {
        items: cart,
        customerInfo: {
          name: `${vendor.name} Customer`,
          email: 'customer@example.com',
          phone: '555-1234'
        },
        paymentMethod: 'card'
      };
      
      let result;
      if (vendor.posSystem === 'clover') {
        result = await createCloverVendorOrder(vendor.id, orderData);
      } else {
        result = await createVendorOrder(vendor.id, orderData);
      }
      
      console.log('Order created successfully:', result);
      
      // Clear only this vendor's items from the global cart
      cart.forEach(item => {
        if (onRemoveFromGlobalCart) {
          onRemoveFromGlobalCart(item.id);
        }
      });
      
      // Notify parent component
      if (onOrderComplete) {
        onOrderComplete(result);
      }
      
    } catch (err) {
      console.error('Error creating order:', err);
      setError(err.message);
    } finally {
      setIsProcessingOrder(false);
    }
  };

  const getCurrentItems = () => {
    if (!menuData || !menuData.menu) return [];
    
    if (selectedCategory === 'All Items') {
      return Object.values(menuData.menu).flat();
    }
    
    return menuData.menu[selectedCategory] || [];
  };

  if (loading) {
    return (
      <VendorMenuContainer>
        <LoadingSpinner>
          Loading {vendor?.name} menu...
        </LoadingSpinner>
      </VendorMenuContainer>
    );
  }

  if (error) {
    return (
      <VendorMenuContainer>
        <MenuHeader>
          <VendorName>{vendor?.name}</VendorName>
        </MenuHeader>
        <ErrorMessage>
          Error loading menu: {error}
        </ErrorMessage>
        <div style={{ textAlign: 'center' }}>
          <CloseButton onClick={onClose}>
            Close
          </CloseButton>
        </div>
      </VendorMenuContainer>
    );
  }

  const categories = menuData ? Object.keys(menuData.menu) : [];
  const currentItems = getCurrentItems();

  return (
    <VendorMenuContainer>
      <MenuHeader>
        <VendorName>{vendor?.name}</VendorName>
        <MenuSubtitle>
          Browse our menu and place your order
        </MenuSubtitle>
      </MenuHeader>

      {categories.length > 1 && (
        <CategoryTabs>
          {categories.map(category => (
            <CategoryTab
              key={category}
              $active={selectedCategory === category}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </CategoryTab>
          ))}
        </CategoryTabs>
      )}

      <MenuItemsGrid>
        {currentItems.map(item => (
          <MenuItem key={item.id}>
            <ItemName>{item.name}</ItemName>
            <ItemDescription>{item.description}</ItemDescription>
            <ItemPrice>${item.price}</ItemPrice>
            <AddToCartButton onClick={() => addToCart(item)}>
              Add to Cart
            </AddToCartButton>
          </MenuItem>
        ))}
      </MenuItemsGrid>

      {cart.length > 0 && (
        <CartSection>
          <CartTitle>Shopping Cart</CartTitle>
          {cart.map(item => (
            <CartItem key={item.id}>
              <CartItemInfo>
                <CartItemName>{item.name}</CartItemName>
                <CartItemQuantity>
                  Qty: {item.quantity}
                </CartItemQuantity>
              </CartItemInfo>
              <CartItemPrice>
                ${(parseFloat(item.price) * item.quantity).toFixed(2)}
              </CartItemPrice>
            </CartItem>
          ))}
          <CartTotal>
            <span>Total:</span>
            <span>${calculateTotal()}</span>
          </CartTotal>
          <CheckoutButton 
            onClick={handleCheckout}
            disabled={isProcessingOrder}
          >
            {isProcessingOrder ? 'Processing...' : 'Checkout'}
          </CheckoutButton>
        </CartSection>
      )}

      <div style={{ textAlign: 'center' }}>
        <CloseButton onClick={onClose}>
          Close Menu
        </CloseButton>
      </div>
    </VendorMenuContainer>
  );
};

export default VendorMenu; 